import memoize from 'lodash/memoize'
import { KEYS } from '../config/keys'

/* global google */

class GoogleMapsAPI {
  constructor() {
    this.initialized = false
  }

  init = memoize(() => {
    return new Promise((resolve) => {
      // Avoid including the Google Maps script twice, this is necessary for development.
      if (window.google?.maps) {
        this.onMapsAPILoaded()
        resolve()
      }

      window.onMapsSDKLoaded = () => {
        this.onMapsAPILoaded()
        resolve()
      }

      const scriptElem = document.createElement('script')
      scriptElem.async = true
      scriptElem.setAttribute(
        'src',
        `https://maps.googleapis.com/maps/api/js?key=${this.getApiKey()}&libraries=places,geometry&callback=onMapsSDKLoaded`
      )
      document.body.appendChild(scriptElem)
    })
  })

  getApiKey() {
    return KEYS.googleMapsApiKey
  }

  onMapsAPILoaded() {
    this.initialized = true

    const map = new google.maps.Map(document.createElement('div'))
    const placesService = new google.maps.places.PlacesService(map)
    const autocompleteService = new google.maps.places.AutocompleteService()

    //  promisify some function we'll use
    this._getPlacePredictions = promisifyMapsFunction(
      autocompleteService.getPlacePredictions.bind(autocompleteService)
    )
    this._nearbySearch = promisifyMapsFunction(
      placesService.nearbySearch.bind(placesService)
    )
    this._getDetails = promisifyMapsFunction(
      placesService.getDetails.bind(placesService)
    )
  }

  getPlacePredictions(addressText) {
    this._ensureInitialized()

    const request = {
      input: addressText,
      componentRestrictions: { country: 'US' },
      fields: ['name', 'address_components', 'geometry'],
    }

    return this._getPlacePredictions(request)
  }

  getPlaceDetail(placeRef) {
    this._ensureInitialized()
    return this._getDetails(placeRef)
  }

  nearbySearch({ keyword, location }) {
    this._ensureInitialized()

    const search = {
      keyword,
      location,
      rankBy: google.maps.places.RankBy.DISTANCE,
    }

    return this._nearbySearch(search)
  }

  getDistanceMiles(locationA, locationB) {
    const distanceInMeters =
      google.maps.geometry.spherical.computeDistanceBetween(
        locationA,
        locationB
      )
    const distanceInMiles = distanceInMeters * 0.000621371192
    return parseFloat(distanceInMiles.toFixed(1))
  }

  _ensureInitialized() {
    if (!this.initialized) {
      throw new Error(
        'GoogleMapsAPI not initialized. Please call init() first.'
      )
    }
  }
}

class GoogleMapsAPIError extends Error {
  constructor(message, status) {
    super(message)
    this.status = status
  }
}

/**
 * Takes a google maps API function and makes it promise-returning instead of using old-style callbacks.
 * @param func
 * @return {function(*=): Promise<unknown>}
 */
function promisifyMapsFunction(func) {
  return (request) => {
    return new Promise((resolve, reject) => {
      function callback(result, status) {
        if (status === 'OK') {
          resolve(result)
        } else if (status === 'ZERO_RESULTS') {
          resolve([])
        } else {
          const error = new GoogleMapsAPIError(
            `Google Maps API error: ${status}`
          )
          reject(error)
        }
      }

      func(request, callback)
    })
  }
}

export const googleMapsAPI = new GoogleMapsAPI()
